import { Coordinates, MapDataItem } from '@bpd-library/types';
import {
    ExtendedApartmentBuilding,
    ExtendedAreaModels,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    getApiObjectCenter,
    getApiObjectCoordinates,
} from '@bpd-library/utilities';
import { formatDetailPageUrl } from '@source/utilities/api-formatters';

import { ProjectTotals } from '../wishlist.types';

const STATUS_ORDER = [
    'Available',
    'InOption',
    'Sold',
    'InPreparation',
    'OnSale',
    'ForRent',
    'Rented',
    'NotActive',
];

interface ListSection {
    project: ExtendedProjectPhase;
    constructionNumbers: ExtendedConstructionNumber[];
}

const updateProjectTotals = (listSections: ListSection[], projectTotals: ProjectTotals) => {
    const projectNames: string[] = [];

    listSections.forEach(section => {
        const projectName = section.project.name;

        if (projectName) {
            let projectTotal = projectTotals[projectName];

            if (!projectTotal) {
                projectTotal = new Text();
                projectTotals[projectName] = projectTotal;
            }

            projectTotal.textContent = `${section.constructionNumbers.length}`;

            projectNames.push(projectName);
        }
    });

    for (const [projectName, projectTotal] of Object.entries(projectTotals)) {
        if (projectNames.indexOf(projectName) < 0) {
            projectTotal.textContent = '0';
        }
    }
};

const filterSections = (wishListData: string[], data: ExtendedAreaModels) => {
    const sections: ListSection[] = [];
    const filteredData = data.constructionNumbers.filter(s => wishListData.includes(s.id));

    filteredData.forEach(constructionNumber => {
        const { projectPhaseName } = constructionNumber;
        const listSection = getSectionByProject(sections, projectPhaseName!);

        if (!listSection) {
            sections.push({
                project: data.projectPhases.find(
                    s => s.name === projectPhaseName,
                ) as ExtendedProjectPhase,
                constructionNumbers: [constructionNumber],
            });
        } else {
            listSection.constructionNumbers.push(constructionNumber);
        }
    });

    return sections;
};

const sortSectionsByProjects = (listSections: ListSection[]) => {
    listSections = listSections.sort((sectionA, sectionB) =>
        sectionA.project?.status &&
        sectionB.project?.status &&
        STATUS_ORDER.indexOf(sectionA.project?.status) >
            STATUS_ORDER.indexOf(sectionB.project?.status)
            ? 1
            : -1,
    );
};

const sortSectionsByConstructionNumbers = (listSections: ListSection[]) => {
    listSections.forEach(section => {
        section.constructionNumbers = section.constructionNumbers.sort((a, b) =>
            a.availabilityStatus &&
            b.availabilityStatus &&
            STATUS_ORDER.indexOf(a.availabilityStatus) > STATUS_ORDER.indexOf(b.availabilityStatus)
                ? 1
                : -1,
        );
    });
};

const getSectionByProject = (data: ListSection[], projectName: string) =>
    data.find(section => section.project?.name === projectName);

const createConstructionNumberPolygon = (
    constructionNumber: ExtendedConstructionNumber,
): MapDataItem | undefined => {
    const coordinates = getApiObjectCoordinates(constructionNumber);

    if (!coordinates) return;

    return {
        coordinates,
        id: constructionNumber.id,
        variant: 'wishlist',
        url: formatDetailPageUrl({ constructionNumber }),
    };
};

const createPropertyTypePolygon = (propertyType: ExtendedPropertyType): MapDataItem | undefined => {
    const { id } = propertyType;

    const coordinates = getApiObjectCoordinates(propertyType);

    if (!coordinates) return;

    return {
        coordinates,
        id,
        variant: 'wishlist',
        objectType: 'propertyType',
    };
};

const createPolygonMarker = (
    apiObject: ExtendedPropertyType | ExtendedConstructionNumber | ExtendedApartmentBuilding,
): MapDataItem | undefined => {
    const { id, geometry } = apiObject;

    // Coordinates may consist of multiple polygons, grab the first one. Otherwise the marker will float in between polygons.
    let firstCoordinate: Coordinates | undefined;

    if (geometry?.length && geometry[0].coordinates) {
        firstCoordinate = [geometry[0].coordinates] as Coordinates;
    }

    const coordinates = firstCoordinate ? firstCoordinate : getApiObjectCenter(apiObject);
    const variant = 'heartIcon';

    if (!coordinates) return;

    return {
        coordinates,
        id: `marker-${id}`,
        variant,
        objectType: apiObject.objectType,
    };
};

const createApartmentBuildingPolygon = (apartmentBuilding: ExtendedApartmentBuilding) => {
    const { id } = apartmentBuilding;

    const coordinates = getApiObjectCoordinates(apartmentBuilding);

    if (!coordinates) return;

    return {
        coordinates,
        id,
        variant: 'wishlist',
        objectType: 'apartmentBuilding',
    };
};

export {
    ListSection,
    updateProjectTotals,
    filterSections,
    sortSectionsByProjects,
    sortSectionsByConstructionNumbers,
    createConstructionNumberPolygon,
    createPropertyTypePolygon,
    createPolygonMarker,
    createApartmentBuildingPolygon,
};
